/**
 * Character Tabs Component
 * Creates tab navigation for multi-character scenarios
 */

/**
 * Default no-op switch handler (can be overridden via options.onSelect)
 * @param {string|number} characterId
 */
const defaultSwitchHandler = async (characterId) => {
  console.warn('No onSelect handler provided for character tabs, characterId:', characterId);
};

/**
 * Create character switching tabs for multi-character scenarios
 * @param {Array} selectedChars - Array of character objects with id and name
 * @param {Object} currentCharacter - The currently selected character
 * @param {Object} options - Configuration options
 * @param {boolean} [options.includeSingle=false] - Show tabs even for single character
 * @param {Function} [options.onSelect] - Callback when a character tab is selected
 * @param {string|number} [options.activeCharacterId] - Override active character ID
 * @returns {HTMLElement|null} The tabs container element or null
 */
export const createCharacterTabs = (selectedChars, currentCharacter, options = {}) => {
  if (!selectedChars || selectedChars.length === 0) {
    return null;
  }

  const { 
    includeSingle = false, 
    onSelect = defaultSwitchHandler, 
    activeCharacterId = currentCharacter?.id 
  } = options;

  if (!includeSingle && selectedChars.length <= 1) {
    return null;
  }

  const tabsContainer = document.createElement('div');
  tabsContainer.className = 'character-tabs-container';

  const resolvedActiveId = activeCharacterId ?? currentCharacter?.id;

  selectedChars.forEach((char, index) => {
    const tabButton = document.createElement('button');
    tabButton.className = 'character-tab-button';
    tabButton.setAttribute('data-character-id', char.id);
    tabButton.dataset.colorIndex = String(index % 3);

    const isActive = resolvedActiveId && String(char.id) === String(resolvedActiveId);

    if (isActive) {
      tabButton.classList.add('active');
    }

    const icon = document.createElement('i');
    icon.className = 'fas fa-user';

    const textSpan = document.createElement('span');
    textSpan.textContent = char.name || `Character ${index + 1}`;

    tabButton.appendChild(icon);
    tabButton.appendChild(textSpan);

    tabButton.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!isActive) {
        await onSelect(char.id);
      }
    });

    tabsContainer.appendChild(tabButton);
  });

  return tabsContainer;
};
